import { isEmpty } from 'lodash'
import moment from 'moment'

export const calculateDuration = (startTime, endTime) => {
  const start = moment.utc(startTime)
  const now = moment.utc(endTime)

  // A null start or end (a CALL_STOP socket payload merged over a row that was
  // mid-call at page load) must not reach format(): moment renders the NaN
  // diff as the literal "Invalid date" (CUS-382). undefined is still "now".
  if (!start.isValid() || !now.isValid()) {
    return '--'
  }

  const duration = moment.duration(now.diff(start))
  const milliseconds = duration.asMilliseconds()

  // Occasionally it will be a negative time initially due to race condition
  if (milliseconds < 1) {
    return '00:00'
  }

  // One hour
  if (milliseconds >= 3_600_000) {
    return moment.utc(duration.asMilliseconds()).format('H:mm:ss')
  }

  return moment.utc(duration.asMilliseconds()).format('mm:ss')
}

export const calculateTimeElapsedStatus = (agent) => {
  if (!agent) {
    return null
  }

  if (agent.call_data?.call_status === 'on_call') {
    return calculateDuration(agent.call_data.call_start_time)
  }

  if (agent.call_data?.call_status === 'had_calls_today') {
    return calculateDuration(agent.call_data.call_start_time, agent.call_data.call_end_time)
  }

  return null
}

export const getTimeElapsedStatus = (agent, timeElapsed) => {
  if (!agent) {
    return null
  }

  if (agent.call_data?.call_status === 'on_call') {
    return timeElapsed
  }

  if (agent.call_data?.call_status === 'had_calls_today') {
    return `Call Duration ${timeElapsed}`
  }

  return 'No calls today'
}

export const NO_OF_FETCHED_CALLS = 10

const audioIssuesMapper = {
  snr_me: "There was a lot of background noise on the agent's end",
  snr_them: "There was a lot of background noise on the customer's end",
  missing_audio_me: "We couldn't pick up the agent's audio",
  missing_audio_them: "We couldn't pick up the customer's audio",
  empty_me: 'The agent went quiet',
  empty_them: 'The customer went quiet',
}

export const getAgentAudioIssueDetails = (agentsAudioIssues = [], agentId) => {
  const counts = {
    snr_me: [],
    snr_them: [],
    missing_audio_me: [],
    missing_audio_them: [],
    empty_me: [],
    empty_them: [],
  }

  const data = agentsAudioIssues.filter((issue) => issue.user_id === agentId)

  for (const issue of data) {
    if (issue.snr_me === true) counts.snr_me.push(issue.call_id)
    if (issue.snr_them === true) counts.snr_them.push(issue.call_id)
    if (issue.missing_audio_me === true) counts.missing_audio_me.push(issue.call_id)
    if (issue.missing_audio_them === true) counts.missing_audio_them.push(issue.call_id)
    if (issue.empty_me === true) counts.empty_me.push(issue.call_id)
    if (issue.empty_them === true) counts.empty_them.push(issue.call_id)
  }

  const audioIssues = []
  const callsWithAudioIssues = new Set()

  Object.entries(counts).forEach(([key, value]) => {
    if (value.length > 0) {
      audioIssues.push(
        `${audioIssuesMapper[key]} on ${value.length} of ${NO_OF_FETCHED_CALLS} calls`
      )
      value.forEach((callId) => callsWithAudioIssues.add(callId))
    }
  })

  return {
    audioIssues,
    callsWithIssues: Array.from(callsWithAudioIssues),
    hasAudioIssues: !isEmpty(audioIssues),
  }
}
